import { useCallback, useEffect } from 'react'
import { fetchWritingSampleReferenceContent } from '@/agent/references/fetch/fetch-writing-sample-reference-content'
import { useWritingSamplePdfLoader } from '@/content-viewer/hooks/useWritingSamplePdfLoader'
import ContentViewerLoadingSpinner from '@/content-viewer/components/common/ContentViewerLoadingStates'
import ContentViewerErrorState from '@/content-viewer/components/common/ContentViewerErrorState'
import RenderReactPdfViewer from '@/components/react-pdf-viewer/render-react-pdf-viewer'

type WritingSamplePdfRendererProps = {
  referenceId: string
  matterId: string
  title: string
  startingPage?: number
  onFileNameLoaded?: (fileName: string) => void
  loadingSize?: 'small' | 'large'
}

function withPdfExtension(fileName: string) {
  return fileName.toLowerCase().endsWith('.pdf') ? fileName : `${fileName}.pdf`
}

export default function WritingSamplePdfRenderer(props: WritingSamplePdfRendererProps) {
  const { referenceId, matterId, title, startingPage, onFileNameLoaded, loadingSize = 'small' } = props
  const fallbackFileName = withPdfExtension(title || referenceId)
  const loadContent = useCallback(() => fetchWritingSampleReferenceContent(referenceId, matterId), [matterId, referenceId])
  const { loading, error, pdfFile, fileName, retry } = useWritingSamplePdfLoader({
    fallbackFileName,
    loadContent,
    sentryContext: { title, referenceId, matterId },
  })

  useEffect(() => {
    if (fileName) onFileNameLoaded?.(fileName)
  }, [fileName, onFileNameLoaded])

  if (loading) {
    return <ContentViewerLoadingSpinner size={loadingSize} />
  }

  if (error || !pdfFile) {
    return <ContentViewerErrorState message="Failed to load PDF file." onRetry={retry} />
  }

  return <RenderReactPdfViewer pdfFile={pdfFile} highlightAreas={[]} startingPage={startingPage} />
}
